import * as React from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Crosshair, Trophy } from 'lucide-react'
import {
  aimAt,
  BARREL,
  fire,
  GROUND_Y,
  HEIGHT,
  muzzle,
  newGame,
  start,
  step,
  TARGET,
  TURRET_X,
  TURRET_Y,
  WIDTH,
  type GameState,
  type Status,
} from '@/lib/missile-game'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'

/**
 * Missile Defence (migrations/0060_missile_defence_game.sql).
 *
 * The opposite of Sheep Race in every way that matters to the code: one
 * player, no session, nothing stored. The catalogue row is the only thing in
 * the database; the rules are in `lib/missile-game.ts` and the round lives in
 * a ref for as long as this page is open.
 *
 * The state is a ref rather than React state because it changes sixty times a
 * second and only the canvas needs to see that. React hears about the two
 * things the HUD shows — the status and the count — and only when they change.
 */
export function MissileDefencePage() {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null)
  const game = React.useRef<GameState>(newGame())
  const [status, setStatus] = React.useState<Status>('ready')
  const [destroyed, setDestroyed] = React.useState(0)

  const begin = React.useCallback(() => {
    game.current = start(game.current)
  }, [])

  // The loop. It runs while the page is mounted whatever the status, because
  // `step` is a no-op outside a round and the barrel should follow the mouse on
  // the start screen too.
  React.useEffect(() => {
    let frame = 0
    let last = performance.now()
    const tick = (now: number) => {
      game.current = step(game.current, (now - last) / 1000, Math.random)
      last = now
      const g = game.current
      setStatus((s) => (s === g.status ? s : g.status))
      setDestroyed((d) => (d === g.destroyed ? d : g.destroyed))
      if (canvasRef.current) draw(canvasRef.current, g)
      frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [])

  // The spacebar fires — on the window, so the player does not have to click
  // the canvas first. A held key fires once: auto-repeat would turn aiming into
  // hosing. Typing in a field elsewhere on the page is left alone.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== 'Space') return
      const t = e.target as HTMLElement | null
      if (t && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName))) return
      // Space scrolls the page by default, which would carry the playfield out
      // from under the mouse mid-round.
      e.preventDefault()
      if (e.repeat) return
      // The spacebar starts the first round but never restarts one: the last
      // frantic press of a round would otherwise wipe its result off the screen
      // before anybody had read it.
      const g = game.current
      if (g.status === 'playing') game.current = fire(g)
      else if (g.status === 'ready') game.current = start(g)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  function onPointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    // The canvas is drawn at WIDTH x HEIGHT and stretched to its box, so the
    // pointer has to be mapped back into world units.
    const rect = e.currentTarget.getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) * WIDTH
    const y = ((e.clientY - rect.top) / rect.height) * HEIGHT
    game.current = aimAt(game.current, x, y)
  }

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon-sm" asChild aria-label="Back to Games">
          <Link to="/games">
            <ArrowLeft />
          </Link>
        </Button>
        <div className="min-w-0">
          <h1 className="text-xl font-semibold leading-tight">Missile Defence</h1>
          <p className="text-xs text-muted-foreground">Aim with the mouse. Fire with the spacebar.</p>
        </div>
        <div className="ml-auto">
          <Badge variant={status === 'won' ? 'success' : 'secondary'} className="tabular-nums">
            <Crosshair className="mr-1 h-3.5 w-3.5" />
            {destroyed} / {TARGET}
          </Badge>
        </div>
      </div>

      <div className="relative overflow-hidden rounded-lg border bg-card">
        <canvas
          ref={canvasRef}
          width={WIDTH}
          height={HEIGHT}
          onPointerMove={onPointerMove}
          className="block h-auto w-full cursor-crosshair touch-none"
          aria-label="Missile Defence playfield"
        />
        {status !== 'playing' && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/70 p-4">
            <div className="max-w-sm space-y-3 text-center">
              {status === 'ready' && (
                <>
                  <p className="text-lg font-semibold">Shoot down {TARGET} missiles.</p>
                  <p className="text-sm text-muted-foreground">
                    Let one reach the ground and the round is over. They fall faster, and more at once,
                    the closer you get.
                  </p>
                </>
              )}
              {status === 'won' && (
                <>
                  <Trophy className="mx-auto h-8 w-8 text-primary" />
                  <p className="text-lg font-semibold">All {TARGET} down. You win.</p>
                </>
              )}
              {status === 'lost' && (
                <>
                  <p className="text-lg font-semibold">One got through.</p>
                  <p className="text-sm text-muted-foreground">
                    {destroyed} of {TARGET} shot down.
                  </p>
                </>
              )}
              <Button onClick={begin}>{status === 'ready' ? 'Start' : 'Play again'}</Button>
              {status === 'ready' && <p className="text-xs text-muted-foreground">or press the spacebar</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * The theme's tokens are HSL triplets (index.css), read at draw time so the
 * playfield follows a switch to dark mode mid-round like the rest of the page.
 */
function token(name: string) {
  return `hsl(${getComputedStyle(document.documentElement).getPropertyValue(name).trim()})`
}

// Exported for Team Missile Defence (missile-defence-team.tsx), which has to
// look like this game and so draws with exactly this.
export function draw(canvas: HTMLCanvasElement, g: GameState) {
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  const ink = token('--foreground')
  const muted = token('--muted-foreground')
  const danger = token('--destructive')
  const turret = token('--primary')

  ctx.clearRect(0, 0, WIDTH, HEIGHT)

  // Ground.
  ctx.strokeStyle = muted
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(0, GROUND_Y)
  ctx.lineTo(WIDTH, GROUND_Y)
  ctx.stroke()

  // Missiles: a trail back up their path and a head, so the direction of travel
  // reads at a glance — leading a diagonal target is the skill.
  for (const m of g.missiles) {
    const speed = Math.hypot(m.vx, m.vy) || 1
    ctx.strokeStyle = danger
    ctx.globalAlpha = 0.35
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.moveTo(m.x - (m.vx / speed) * 40, m.y - (m.vy / speed) * 40)
    ctx.lineTo(m.x, m.y)
    ctx.stroke()
    ctx.globalAlpha = 1
    ctx.fillStyle = danger
    ctx.beginPath()
    ctx.arc(m.x, m.y, 6, 0, Math.PI * 2)
    ctx.fill()
  }

  // Shots.
  ctx.fillStyle = ink
  for (const s of g.shots) {
    ctx.beginPath()
    ctx.arc(s.x, s.y, 3, 0, Math.PI * 2)
    ctx.fill()
  }

  // Where the round was lost.
  if (g.impactX != null) {
    ctx.fillStyle = danger
    ctx.globalAlpha = 0.3
    ctx.beginPath()
    ctx.arc(g.impactX, GROUND_Y, 28, Math.PI, 0)
    ctx.fill()
    ctx.globalAlpha = 1
  }

  // Turret: barrel first, so the dome sits over its root.
  const tip = muzzle(g.aim)
  ctx.strokeStyle = turret
  ctx.lineWidth = 8
  ctx.lineCap = 'round'
  ctx.beginPath()
  ctx.moveTo(TURRET_X, TURRET_Y)
  ctx.lineTo(tip.x, tip.y)
  ctx.stroke()
  ctx.lineCap = 'butt'
  ctx.fillStyle = turret
  ctx.beginPath()
  ctx.arc(TURRET_X, TURRET_Y, BARREL * 0.55, Math.PI, 0)
  ctx.fill()
}
